/**
 * Sentry Performance Tracking Hooks
 *
 * Custom React hooks for tracking performance metrics in Sentry.
 * Follows the existing usePostHog() hook pattern for consistency.
 *
 * Features:
 * - Screen-level performance tracking (critical screens only)
 * - API call monitoring with timing and status
 * - Cross-referencing with PostHog session IDs
 * - Automatic cleanup on unmount
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */

import * as Sentry from '@sentry/react-native';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import logger from '../utils/logger';

/**
 * Hook for tracking screen-level performance metrics.
 *
 * Only tracks critical screens to preserve quota. Creates a Sentry transaction
 * that measures time spent on screen and captures performance data.
 *
 * @param {string} screenName - Name of the screen (e.g., 'Dashboard', 'ProductDetail')
 * @param {boolean} isCritical - Whether this is a critical screen to track (default: false)
 *
 * @example
 * ```javascript
 * function DashboardScreen() {
 *   useSentryScreenTracking('Dashboard', true);
 *   // ... rest of component
 * }
 * ```
 *
 * @example
 * ```javascript
 * function SettingsScreen() {
 *   // Non-critical screen - won't be tracked
 *   useSentryScreenTracking('Settings', false);
 *   // ... rest of component
 * }
 * ```
 *
 * @requirement 3.1 (Frontend performance monitoring)
 * @requirement 3.2 (Track time to first recommendation)
 * @requirement 3.6 (Cross-reference with PostHog)
 */
export function useSentryScreenTracking(screenName, isCritical = false) {
  const posthog = usePostHog();
  const transactionRef = useRef(null);

  useEffect(() => {
    // Only track if screen is marked as critical
    if (!isCritical) {
      logger.log(`[Sentry] Screen "${screenName}" is not critical - skipping performance tracking`);
      return;
    }

    // Check if performance tracking APIs are available (native only)
    if (typeof Sentry.startTransaction !== 'function') {
      logger.log(
        `[Sentry] Screen tracking for "${screenName}" skipped - startTransaction not available on web`
      );
      return;
    }

    try {
      // Get PostHog session ID for cross-referencing
      const posthogSessionId = posthog?.getSessionId?.() || 'unknown';

      // Start a new transaction for this screen
      const transaction = Sentry.startTransaction({
        name: `Screen: ${screenName}`,
        op: 'navigation',
        tags: {
          screen: screenName,
          platform: Platform.OS,
          posthog_session_id: posthogSessionId,
        },
      });

      // Store transaction reference for cleanup
      transactionRef.current = transaction;

      logger.log(
        `[Sentry] Started performance tracking for screen "${screenName}" (PostHog session: ${posthogSessionId})`
      );

      // Set the transaction on the current scope (if API available)
      if (typeof Sentry.getCurrentHub === 'function') {
        Sentry.getCurrentHub().configureScope((scope) => {
          scope.setSpan(transaction);
        });
      }
    } catch (error) {
      logger.error(`[Sentry] Error starting screen tracking for "${screenName}":`, error);
      // Fail silently - performance tracking should never break the app
    }

    // Cleanup: Finish transaction when component unmounts
    return () => {
      try {
        if (transactionRef.current) {
          transactionRef.current.finish();
          logger.log(`[Sentry] Finished performance tracking for screen "${screenName}"`);
          transactionRef.current = null;
        }
      } catch (error) {
        logger.error(`[Sentry] Error finishing screen tracking for "${screenName}":`, error);
      }
    };
  }, [screenName, isCritical, posthog]);
}

/**
 * Hook that returns a callback for tracking API call performance.
 *
 * Wraps API calls with Sentry spans to measure duration, capture errors,
 * and track status codes. Use this for critical API calls like fetching
 * recommendations or user data.
 *
 * @returns {Function} trackApiCall - Function to wrap API calls
 *
 * @example
 * ```javascript
 * function MyComponent() {
 *   const trackApiCall = useSentryApiTracking();
 *
 *   const fetchData = async () => {
 *     return trackApiCall(
 *       'supabase.fetch_recommendations',
 *       async () => {
 *         const response = await apiService.getRecommendations();
 *         return response;
 *       }
 *     );
 *   };
 * }
 * ```
 *
 * @example
 * ```javascript
 * // With error handling
 * const result = await trackApiCall(
 *   'langflow.chat',
 *   async () => {
 *     const response = await fetch('/api/chat', { method: 'POST' });
 *     if (!response.ok) throw new Error('API call failed');
 *     return response.json();
 *   }
 * );
 * ```
 *
 * @requirement 3.3 (Track API call latency)
 * @requirement 3.4 (Performance monitoring for all API calls)
 * @requirement 3.5 (Capture errors with stack traces)
 * @requirement 3.7 (Set span status based on success/failure)
 */
export function useSentryApiTracking() {
  /**
   * Tracks an API call with Sentry performance monitoring.
   *
   * @param {string} apiName - Name of the API call (e.g., 'supabase.fetch_recommendations')
   * @param {Function} apiCallFn - Async function that performs the API call
   * @returns {Promise<any>} Result of the API call
   * @throws {Error} Re-throws any error from the API call after capturing it
   */
  const trackApiCall = useCallback(async (apiName, apiCallFn) => {
    // Get the current transaction (if any) to attach span to
    let transaction = null;
    if (typeof Sentry.getCurrentHub === 'function') {
      transaction = Sentry.getCurrentHub().getScope()?.getTransaction();
    }

    let span = null;
    const startTime = Date.now();

    try {
      // Start a span for this API call (if APIs available)
      if (transaction && typeof transaction.startChild === 'function') {
        span = transaction.startChild({
          op: 'api.call',
          description: apiName,
          data: {
            api_name: apiName,
            platform: Platform.OS,
          },
        });
      } else if (typeof Sentry.startTransaction === 'function') {
        // If no active transaction, create a standalone span (native only)
        span = Sentry.startTransaction({
          name: apiName,
          op: 'api.call',
          data: {
            api_name: apiName,
            platform: Platform.OS,
          },
        });
      }

      if (span) {
        logger.log(`[Sentry] Starting API call tracking: ${apiName}`);
      }

      // Execute the API call
      const result = await apiCallFn();
      const duration = Date.now() - startTime;

      // Mark span as successful
      if (span) {
        span.setStatus('ok');
        span.setData('duration_ms', duration);
        logger.log(`[Sentry] API call successful: ${apiName} (${duration}ms)`);
      }

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;

      // Mark span as error
      if (span) {
        span.setStatus('internal_error');
        span.setData('error', error.message);
        span.setData('duration_ms', duration);
      }

      logger.error(`[Sentry] API call failed: ${apiName} (${duration}ms)`, error);

      // Capture exception to Sentry with additional context (always works)
      Sentry.captureException(error, {
        tags: {
          api_name: apiName,
          api_call_failed: true,
        },
        contexts: {
          api: {
            name: apiName,
            platform: Platform.OS,
            duration_ms: duration,
          },
        },
      });

      // Re-throw the error so the caller can handle it
      throw error;
    } finally {
      // Always finish the span
      if (span && typeof span.finish === 'function') {
        span.finish();
      }
    }
  }, []);

  return trackApiCall;
}

/**
 * Export all performance tracking hooks as default for easy testing
 */
export default {
  useSentryScreenTracking,
  useSentryApiTracking,
};
